import { Reason } from '../../misc/reason/implementation';
import { CANCELLABLE_PROMISE_PRIVATE } from './privates';
import { ConstructCancellablePromise, IsCancellablePromise, NewCancellablePromiseFromInstance } from './constructor';
import { AdvancedAbortController } from '../../misc/advanced-abort-controller/implementation';
import { Finally, PromiseTry } from '../helpers';
import { NormalizeICancellablePromiseFinallyOptions } from './functions';
import { IsObject } from '../../helpers';
function CancellablePromiseInternalThen(instance, onFulfilled, onRejected, onCancelled) {
    const privates = instance[CANCELLABLE_PROMISE_PRIVATE];
    const _onFulfilled = (typeof onFulfilled === 'function')
        ? privates.signal.wrapFunction((value) => {
            return onFulfilled.call(instance, value, privates.signal);
        }, { strategy: privates.strategy })
        : void 0;
    const _onRejected = (typeof onRejected === 'function')
        ? privates.signal.wrapFunction((reason) => {
            return onRejected.call(instance, reason, privates.signal);
        }, { strategy: privates.strategy })
        : void 0;
    const fulfilledAndRejectedHandledPromise = ((_onFulfilled === void 0) && (_onFulfilled === void 0))
        ? privates.promise
        : privates.promise.then(_onFulfilled, _onRejected);
    let newSignal;
    let cancelHandledPromise;
    if (typeof onCancelled === 'function') {
        const abortController = new AdvancedAbortController();
        newSignal = abortController.signal;
        cancelHandledPromise = privates.signal.wrapPromise(fulfilledAndRejectedHandledPromise, {
            strategy: privates.strategy,
            onAborted: (reason, newController) => {
                return onCancelled.call(instance, reason, newController, privates.signal);
            },
            onAbortedController: abortController
        });
    }
    else {
        newSignal = privates.signal;
        cancelHandledPromise = fulfilledAndRejectedHandledPromise;
    }
    return NewCancellablePromiseFromInstance(instance, cancelHandledPromise, { signal: newSignal });
}
function CancellablePromiseOptimizedThen(instance, onFulfilled, onRejected, onCancelled) {
    const privates = instance[CANCELLABLE_PROMISE_PRIVATE];
    return privates.isCancellablePromiseWithSameSignal
        ? privates.promise.then(onFulfilled, onRejected, onCancelled)
        : CancellablePromiseInternalThen(instance, onFulfilled, onRejected, onCancelled);
}
function CancellablePromiseInternalFinally(instance, onFinally, options) {
    if (typeof onFinally === 'function') {
        const _options = NormalizeICancellablePromiseFinallyOptions(options);
        const privates = instance[CANCELLABLE_PROMISE_PRIVATE];
        return CancellablePromiseThen(instance, (value) => {
            return PromiseTry(() => {
                return onFinally.call(instance, {
                    status: 'fulfilled',
                    value: value
                }, privates.signal);
            }).then(() => value);
        }, (reason) => {
            return PromiseTry(() => {
                return onFinally.call(instance, {
                    status: 'rejected',
                    reason: reason
                }, privates.signal);
            }).then(() => {
                throw reason;
            });
        }, _options.includeCancelled
            ? (reason, newController) => {
                return PromiseTry(() => {
                    return onFinally.call(instance, {
                        status: 'cancelled',
                        reason: reason
                    }, privates.signal);
                }).then(() => {
                    newController.abort(reason);
                    throw new Error(`Cancelled`);
                });
            }
            : void 0);
    }
    else {
        return CancellablePromiseThen(instance, void 0, void 0, void 0);
    }
}
function CancellablePromiseOptimizedFinally(instance, onFinally, options) {
    const privates = instance[CANCELLABLE_PROMISE_PRIVATE];
    return privates.isCancellablePromiseWithSameSignal
        ? privates.promise.finally(onFinally, options)
        : CancellablePromiseInternalFinally(instance, onFinally, options);
}
function CancellablePromiseRunFactories(constructor, factories, options) {
    return factories.map((factory) => {
        return CancellablePromiseTry(constructor, factory, options);
    });
}
function AbortControllerWhenPromiseResolved(promise, controller, getReason) {
    return promise
        .then(...Finally(() => {
        if (!controller.signal.aborted) {
            controller.abort(getReason());
        }
    }));
}
export function CancellablePromiseThen(instance, onFulfilled, onRejected, onCancelled) {
    return CancellablePromiseOptimizedThen(instance, onFulfilled, onRejected, onCancelled);
}
export function CancellablePromiseCatch(instance, onRejected) {
    return CancellablePromiseThen(instance, void 0, onRejected, void 0);
}
export function CancellablePromiseCancelled(instance, onCancelled) {
    return CancellablePromiseThen(instance, void 0, void 0, onCancelled);
}
export function CancellablePromiseFinally(instance, onFinally, options) {
    return CancellablePromiseOptimizedFinally(instance, onFinally, options);
}
export function CancellablePromiseOf(constructor, promiseOrCallback, options) {
    return (IsCancellablePromise(promiseOrCallback)
        && IsObject(options)
        && (promiseOrCallback.signal === options.signal))
        ? promiseOrCallback
        : new constructor(promiseOrCallback, options);
}
export function CancellablePromiseResolve(constructor, value, options) {
    return new CancellablePromise(Promise.resolve(value), options);
}
export function CancellablePromiseReject(constructor, reason, options) {
    return new CancellablePromise(Promise.reject(reason), options);
}
export function CancellablePromiseTry(constructor, callback, options) {
    return new constructor(function (resolve, reject, signal) {
        resolve(callback.call(this, signal));
    }, options);
}
export function CancellablePromiseRace(constructor, factories, options) {
    return CancellablePromiseTry(constructor, function (signal) {
        const controller = AdvancedAbortController.fromAbortSignals(signal);
        return AbortControllerWhenPromiseResolved(Promise.race(CancellablePromiseRunFactories(constructor, factories, {
            ...options,
            signal: controller.signal
        })), controller, () => new Reason(`One of the racing promises is resolved`, 'RACE_CALLBACK_RESOLVED'));
    }, options);
}
export function CancellablePromiseAll(constructor, factories, options) {
    return CancellablePromiseTry(constructor, function (signal) {
        const controller = AdvancedAbortController.fromAbortSignals(signal);
        return AbortControllerWhenPromiseResolved(Promise.all(CancellablePromiseRunFactories(constructor, factories, {
            ...options,
            signal: controller.signal
        })), controller, () => new Reason(`One of the parallel promises is rejected`, 'ALL_CALLBACK_REJECTED'));
    }, options);
}
export function CancellablePromiseFetch(constructor, requestInfo, requestInit, options) {
    return new constructor((resolve, reject, signal) => {
        resolve(fetch(...signal.wrapFetchArguments(requestInfo, requestInit)));
    }, options);
}
export class CancellablePromise {
    static of(promiseOrCallback, options) {
        return CancellablePromiseOf(this, promiseOrCallback, options);
    }
    static resolve(value, options) {
        return CancellablePromiseResolve(this, value, options);
    }
    static reject(reason, options) {
        return CancellablePromiseReject(this, reason, options);
    }
    static try(callback, options) {
        return CancellablePromiseTry(this, callback, options);
    }
    static race(factories, options) {
        return CancellablePromiseRace(this, factories, options);
    }
    static all(values, options) {
        return CancellablePromiseAll(this, values, options);
    }
    static fetch(requestInfo, requestInit, options) {
        return CancellablePromiseFetch(this, requestInfo, requestInit, options);
    }
    constructor(promiseOrCallback, options) {
        ConstructCancellablePromise(this, promiseOrCallback, options);
    }
    get promise() {
        return this[CANCELLABLE_PROMISE_PRIVATE].promise;
    }
    get signal() {
        return this[CANCELLABLE_PROMISE_PRIVATE].signal;
    }
    get [Symbol.toStringTag]() {
        return 'CancellablePromise';
    }
    then(onFulfilled, onRejected, onCancelled) {
        return CancellablePromiseThen(this, onFulfilled, onRejected, onCancelled);
    }
    catch(onRejected) {
        return CancellablePromiseCatch(this, onRejected);
    }
    cancelled(onCancelled) {
        return CancellablePromiseCancelled(this, onCancelled);
    }
    finally(onFinally, options) {
        return CancellablePromiseFinally(this, onFinally, options);
    }
}
//# sourceMappingURL=implementation.js.map